import React, { useState, useEffect } from 'react';
import { FiArrowLeft, FiShoppingCart, FiChevronRight } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import API from '../api/axios';
import { formatPrice, formatDateTime, ORDER_STATUS_MAP } from '../utils/constants';
import './Orders.css';



const Orders = () => {
  const { cart, updateUser } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const [cancellationSeconds, setCancellationSeconds] = useState(60);
  const [, setTicker] = useState(0);

  useEffect(() => {
    fetchOrders();
    fetchCancellationPolicy();

    // Ticker to automatically refresh cancellation buttons in real-time
    const interval = setInterval(() => {
      setTicker(t => t + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const fetchCancellationPolicy = async () => {
    try {
      const { data } = await API.get('/print-rates');
      if (data.success && data.rates) {
        const rate = data.rates.find(r => r.key === 'order_cancellation_seconds');
        if (rate) setCancellationSeconds(Number(rate.value));
      }
    } catch (err) {
      console.error('Failed to fetch cancellation policy:', err);
    }
  };

  const fetchOrders = async () => {
    try {
      const { data } = await API.get('/orders');
      setOrders(data.orders || []);
    } catch (error) {
      console.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const cancelOrder = async (orderId) => {
    try {
      const { data } = await API.put(`/orders/${orderId}/cancel`);
      toast.success('Order cancelled. Amount refunded.');
      updateUser({ walletBalance: data.walletBalance });
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to cancel');
    }
  };

  const isCancellable = (order) => {
    if (order.status !== 'confirmed') return false;
    const elapsedSeconds = (Date.now() - new Date(order.createdAt).getTime()) / 1000;
    return elapsedSeconds <= cancellationSeconds;
  };

  const getRemainingSeconds = (createdAt) => {
    const elapsed = (Date.now() - new Date(createdAt).getTime()) / 1000;
    return Math.max(0, Math.ceil(cancellationSeconds - elapsed));
  };

  return (
    <div className="page">
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <FiArrowLeft />
        </button>
        <h1 className="page-title">My Orders</h1>
        {cart.length > 0 && (
          <button
            className="btn btn-primary"
            style={{ padding: '8px 14px', fontSize: 13, borderRadius: 20 }}
            onClick={() => navigate('/checkout')}
          >
            <FiShoppingCart size={14} style={{ marginRight: 4 }} />
            Cart ({cart.length})
          </button>
        )}
      </div>

      <div className="orders-section">
        {loading ? (
          <div className="spinner"></div>
        ) : orders.length === 0 ? (
          <div className="empty-state">
            <div className="icon">📦</div>
            <p>No orders yet.</p>
            <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={() => navigate('/')}>
              Shop Now
            </button>
          </div>
        ) : (
          orders.map(order => {
            const statusInfo = ORDER_STATUS_MAP[order.status] || {};
            const itemCount = order.items.reduce((acc, item) => acc + item.quantity, 0);
            return (
              <div 
                key={order._id} 
                className="order-card order-card-compact"
                onClick={() => navigate(`/orders/${order._id}`)}
              >
                <div className="order-card-header">
                  <div className="order-id-group">
                    <span className="order-id">{order.orderId}</span>
                    <FiChevronRight size={16} className="chevron-icon" />
                  </div>
                  <span className="badge" style={{ background: statusInfo.bg, color: statusInfo.color }}>
                    {statusInfo.label}
                  </span>
                </div>
                
                <div className="order-summary-items">
                  {order.items.map((item, idx) => (
                    <div key={idx} className="item-name-qty">
                      • {item.name} {item.quantity > 1 ? `(x${item.quantity})` : ''}
                    </div>
                  ))}
                </div>

                <div className="order-card-meta">
                  <span className="order-date">Placed: {formatDateTime(order.createdAt)}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span className="order-items-count-badge">{itemCount} {itemCount === 1 ? 'item' : 'items'}</span>
                    <span className="order-amount-total">{formatPrice(order.amount)}</span>
                  </div>
                </div>

                {isCancellable(order) && (
                  <button
                    className="btn btn-danger btn-cancel-compact"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent navigation to detail page
                      cancelOrder(order._id);
                    }}
                  >
                    Cancel Order ({getRemainingSeconds(order.createdAt)}s)
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      <div className="fab-spacer"></div>
    </div>
  );
};

export default Orders;
